import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, interval, fromEvent, merge, from, of, Subscription } from 'rxjs';
import { switchMap, startWith, catchError } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { AppService, CURRENT_USER } from '../app.service';

/**
 * Scope descriptor included in chat responses (additive — may be absent on older backend).
 * `level` is the current backend field; `kind` is accepted for backward-compat with
 * older responses. The chat UI normalises across both (see scopeLabel).
 */
export interface ChatScope {
  /** Current backend field. */
  level?: 'account' | 'campaign';
  /** Legacy field (older backend). */
  kind?: 'account' | 'campaign';
  project_id: string | null;
  project_name: string | null;
}

export interface AssistantReply {
  assistant_message: string;
  mode: string;
  quick_replies?: { id: string; label: string }[];
  data?: any[];
  download?: { filename: string; content?: string; base64?: string; mime?: string; encoding?: string };
  project_id?: string;
  project_name?: string;
  usage?: any;
  /** Scope of the answer — account-wide or campaign-specific. Added in backend v180+. */
  scope?: ChatScope;
  /** Unique ID of this in-flight request, used to cancel via /chat/cancel. */
  request_id?: string;
}

export interface ChatTurn {
  role: 'user' | 'assistant';
  text: string;
}

/** A persisted conversation turn returned by the history endpoint. */
export interface HistoryTurn {
  role: 'user' | 'assistant';
  text: string;
  ts?: number | string;
}

/**
 * Paginated response from GET /v1/assistant/history.
 * `history` is oldest→newest within the requested window. `offset` counts
 * turns back from the newest (0 = newest page). `has_more` => older turns exist.
 */
export interface HistoryPage {
  history: HistoryTurn[];
  count: number;
  has_more: boolean;
  offset: number;
  total: number;
}

export interface NotificationItem {
  id: string;
  text: string;
  kind: string;
  created_at: string;
  read: boolean;
}

// ── Setup section types ──────────────────────────────────────────────────────

/** A single input parameter declared on a webhook skill. */
export interface SkillInputParam {
  name: string;
  type: string;
  description?: string;
}

/** A skill the entity has configured (webhook, builtin, or instruction). */
export interface AssistantSkill {
  skill_id: string;
  type?: 'webhook' | 'builtin' | 'instruction';
  name?: string;
  description?: string;
  enabled: boolean;
  method?: string;
  url?: string;
  trigger_keywords?: string[];
  input_params?: SkillInputParam[];
  secret_ref?: string;
  builtin_key?: string;
}

/** Per-period usage figures from /v1/assistant/usage. */
export interface UsagePeriod {
  total_tokens: number;
  cost_usd: number;
  cost_inr: number;
  calls: number;
}

export interface UsageReport {
  today?: UsagePeriod;
  week?: UsagePeriod;
  month?: UsagePeriod;
  model?: string;
}

/** A scheduled routine the entity has configured (created via chat). */
export interface AssistantRoutine {
  routine_id: string;
  name?: string;
  schedule?: { type?: string; time_ist?: string; [k: string]: any };
  action?: { type?: string; [k: string]: any };
  enabled: boolean;
}

/** A capability toggle entry returned by GET /v1/assistant/admin/capabilities. */
export interface AdminCapability {
  key: string;
  label: string;
  description?: string;
  enabled: boolean;
}

/**
 * Friendly, surfaced API error. `message` is safe to show inline (it carries
 * the backend's validation text, e.g. SSRF "url host resolves to a blocked
 * address"); never contains tokens or secret values.
 */
export class AssistantApiError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.name = 'AssistantApiError';
    this.status = status;
  }
}

/**
 * Talks to the Arya campaign-operator assistant (AWS Lambda).
 *
 * Auth: sends the stored JWT as "Authorization: Bearer <token>".
 * The token is stored as "bearer <jwt>" by AppService.setCurrentAccount —
 * we strip the "bearer " prefix before forwarding so the Lambda sees a
 * clean Bearer token.
 *
 * Entity scope and owner name are read from the h4u-current-user profile
 * in localStorage so no extra API call is needed.
 */
@Injectable({ providedIn: 'root' })
export class AssistantService {
  readonly unread$ = new BehaviorSubject<number>(0);

  private pollingSub: Subscription | null = null;

  constructor(private http: HttpClient, private app: AppService) {}

  /** Raw JWT without "bearer " prefix, ready for Authorization header. */
  private getJwt(): string {
    const raw = localStorage.getItem('jwtToken') || '';
    return raw.replace(/^bearer\s+/i, '').trim();
  }

  /** Owner's first/display name from the stored profile. */
  getOwnerName(): string {
    try {
      const cu = JSON.parse(localStorage.getItem(CURRENT_USER) || 'null');
      if (!cu) { return ''; }
      const u = cu.user || cu.profile || {};
      const um = (u && u.userMeta) || (cu && cu.userMeta) || {};
      const name = String(
        cu.first_name || cu.firstName || cu.fname ||
        u.first_name  || u.firstName  ||
        um.firstName  || um.first_name || um.name ||
        cu.name || u.name || cu.full_name || u.full_name || ''
      ).trim();
      if (name) { return name.split(/\s+/)[0]; }
      // fall back to email local-part
      const em = (u && u.email) || (cu && cu.email) || '';
      if (em && em.indexOf('@') > 0) {
        const lp = em.split('@')[0].replace(/[._-]+/g, ' ').trim();
        if (/^[a-zA-Z]/.test(lp)) { return lp.split(/\s+/)[0]; }
      }
    } catch (_) {}
    return '';
  }

  private authHeaders(): HttpHeaders {
    return new HttpHeaders({ Authorization: `Bearer ${this.getJwt()}` });
  }

  startNotificationPolling(): void {
    if (this.pollingSub && !this.pollingSub.closed) { return; }
    this.pollingSub = merge(interval(60000), fromEvent(window, 'focus'))
      .pipe(
        startWith(0),
        switchMap(() =>
          from(this.fetchAndAckNotifications(false)).pipe(catchError(() => of([]))),
        ),
      )
      .subscribe();
  }

  stopNotificationPolling(): void {
    if (this.pollingSub) {
      this.pollingSub.unsubscribe();
      this.pollingSub = null;
    }
  }

  /** Entity ID for the logged-in user.
   * Primary source: AppService.getEntityId() (reads h4u-current-user from localStorage).
   * Fallback: parse h4u-current-user directly so polling works even when the
   * Angular service has not yet set its BehaviorSubject (e.g. on first tick). */
  private getEntityId(): string {
    try {
      const fromService = this.app.getEntityId();
      if (fromService) { return fromService; }
    } catch (_) {}
    try {
      const raw = localStorage.getItem('h4u-current-user');
      if (!raw) { return ''; }
      const parsed = JSON.parse(raw);
      return (parsed && parsed.entity && parsed.entity.id) || '';
    } catch (_) { return ''; }
  }

  async fetchAndAckNotifications(ackAll: boolean): Promise<NotificationItem[]> {
    try {
      const entityId = this.getEntityId();
      if (!entityId) { return []; }
      const url = `${environment.assistantApiUrl}/notifications?entity_id=${encodeURIComponent(entityId)}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${this.getJwt()}` },
      });
      if (!res.ok) { return []; }
      const data: { unread: number; items: NotificationItem[] } = await res.json();
      this.unread$.next(data.unread || 0);
      const items = data.items || [];
      if (ackAll && items.length > 0) {
        await this.ackNotifications();
      }
      return items;
    } catch (_) {
      return [];
    }
  }

  async ackNotifications(ids?: string[]): Promise<void> {
    try {
      const url = `${environment.assistantApiUrl}/notifications/ack`;
      const entityId = this.getEntityId();
      if (!entityId) { return; }
      const body: { ids?: string[]; entity_id: string } = ids
        ? { ids, entity_id: entityId }
        : { entity_id: entityId };
      const res = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${this.getJwt()}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (res.ok) {
        this.unread$.next(0);
      }
      // On failure: leave unread count so next poll self-heals.
    } catch (_) {}
  }

  /**
   * GET /v1/assistant/history — prior conversation turns (oldest→newest within
   * the requested window), paginated.
   *
   * `offset` counts turns back from the newest: offset=0 → newest page;
   * offset=40 → the 40 turns before those (older). Used to load older history
   * on scroll-up.
   *
   * Returns an empty page ({ history: [], count: 0, has_more: false, ... }) on
   * any failure (missing entity, network, non-200) so callers can fall back to
   * showing the greeting as a fresh conversation and never crash mid-scroll.
   */
  async history(limit = 100, offset = 0): Promise<HistoryPage> {
    const empty: HistoryPage = { history: [], count: 0, has_more: false, offset, total: 0 };
    try {
      const entityId = this.getEntityId();
      if (!entityId) { return empty; }
      const url = `${environment.assistantApiUrl}/v1/assistant/history`
        + `?limit=${encodeURIComponent(String(limit))}`
        + `&offset=${encodeURIComponent(String(offset))}`
        + `&entity_id=${encodeURIComponent(entityId)}`;
      const res = await fetch(url, {
        headers: { Authorization: `Bearer ${this.getJwt()}` },
        cache: 'no-store',
      });
      if (!res.ok) { return empty; }
      const data: Partial<HistoryPage> = await res.json();
      const items = (data && data.history) || [];
      // Keep only well-formed turns with non-empty text.
      const clean = items.filter(
        t => t && (t.role === 'user' || t.role === 'assistant') && !!(t.text && String(t.text).trim()),
      );
      return {
        history: clean,
        count: typeof data.count === 'number' ? data.count : clean.length,
        has_more: !!data.has_more,
        offset: typeof data.offset === 'number' ? data.offset : offset,
        total: typeof data.total === 'number' ? data.total : clean.length,
      };
    } catch (_) {
      return empty;
    }
  }

  /** GET /v1/assistant/menu — fetch the top-level menu chips on open. */
  menu() {
    const url = `${environment.assistantApiUrl}/v1/assistant/menu`;
    return this.http.get<AssistantReply>(url, { headers: this.authHeaders() }).toPromise();
  }

  /** GET /v1/assistant/channels — delivery-channel connection status (WhatsApp). */
  getChannels(): Promise<{ channels?: { whatsapp?: { connected: boolean; stale: boolean; number?: string; connect_route?: string } } }> {
    return this.apiRequest('GET', '/v1/assistant/channels');
  }

  // ── WhatsApp recipient number (scheduled reports are sent TO this number) ────
  // The QR-connect flow was dropped (2026-06-24) in favour of a saved recipient
  // number. Endpoints may 404 until the backend lands — callers degrade gracefully.

  /** GET /v1/assistant/whatsapp/recipient — load the saved recipient number. */
  getWhatsappRecipient(): Promise<{ phone?: string }> {
    return this.apiRequest('GET', '/v1/assistant/whatsapp/recipient');
  }

  /** PUT /v1/assistant/whatsapp/recipient — save the recipient number. */
  setWhatsappRecipient(phone: string): Promise<{ phone?: string }> {
    return this.apiRequest('PUT', '/v1/assistant/whatsapp/recipient', {
      body: { entity_id: this.getEntityId(), phone },
    });
  }

  /** DELETE /v1/assistant/whatsapp/recipient — remove the saved recipient number. */
  deleteWhatsappRecipient(): Promise<void> {
    return this.apiRequest('DELETE', '/v1/assistant/whatsapp/recipient');
  }

  // ── WhatsApp QR / go_bridge connect ───────────────────────────────────────

  /**
   * POST /v1/assistant/whatsapp/connect — start (or restart) a QR session.
   * Returns `{ qr_data_url?: string; status?: string }` where qr_data_url is
   * a data-URI (data:image/png;base64,…) ready for an <img> src. The backend
   * may also return the QR inline here; if absent, call getWhatsappQr().
   */
  createWhatsappSession(): Promise<{ qr_data_url?: string; status?: string }> {
    return this.apiRequest('POST', '/v1/assistant/whatsapp/connect', { body: {} });
  }

  /**
   * GET /v1/assistant/whatsapp/qr — fetch (or re-fetch) the current QR image.
   * Returns `{ qr_data_url?: string }`. 404 means the backend endpoint is
   * absent (Serri path only); callers degrade gracefully.
   */
  getWhatsappQr(): Promise<{ qr_data_url?: string }> {
    return this.apiRequest('GET', '/v1/assistant/whatsapp/qr');
  }

  /**
   * GET /v1/assistant/whatsapp/status — poll connection state.
   * Returns `{ authenticated: boolean; number?: string; status?: string }`.
   * `authenticated: true` means the device is paired and messages can be sent.
   */
  getWhatsappStatus(): Promise<{ authenticated: boolean; number?: string; status?: string }> {
    return this.apiRequest('GET', '/v1/assistant/whatsapp/status');
  }

  /** POST /v1/assistant/chat/cancel — cancel an in-flight chat request server-side.
   * entity_id MUST be in the body: the backend's cancel key is entity_id:request_id,
   * and the chat handler registers it from the chat body's entity_id (not the query). */
  cancelChat(requestId: string): Promise<{ cancelled: boolean }> {
    return this.apiRequest('POST', '/v1/assistant/chat/cancel', {
      body: { request_id: requestId, entity_id: this.getEntityId() },
    });
  }

  /** GET /v1/assistant/skill-catalog — predefined starter skills + can_add flag. */
  getSkillCatalog(): Promise<{ catalog?: any[]; can_add?: boolean }> {
    return this.apiRequest('GET', '/v1/assistant/skill-catalog');
  }

  /**
   * POST /v1/assistant/export — generate a downloadable report.
   * Returns {filename, mime, base64} which the caller turns into a download.
   */
  exportReport(dataType: string, format: 'csv' | 'xlsx' | 'pdf'): Promise<{ filename: string; mime: string; base64: string; rows?: number }> {
    return this.apiRequest('POST', '/v1/assistant/export', { body: { data_type: dataType, format } });
  }

  /** POST /v1/assistant/chat */
  chat(
    message: string,
    opts: {
      projectId?: string | null;
      greeted?: boolean;
      ownerName?: string;
      history?: ChatTurn[];
    } = {}
  ) {
    const url = `${environment.assistantApiUrl}/v1/assistant/chat`;
    const body: any = {
      entity_id: this.getEntityId(),
      message,
      greeted: opts.greeted !== undefined ? opts.greeted : false,
    };
    if (opts.projectId)  { body.project_id  = opts.projectId; }
    if (opts.ownerName)  { body.owner_name  = opts.ownerName; }
    if (opts.history && opts.history.length) { body.history = opts.history; }
    return this.http.post<AssistantReply>(url, body, { headers: this.authHeaders() }).toPromise();
  }

  // ── Setup section: shared fetch helper ─────────────────────────────────────

  /**
   * Tenant-safe fetch against the assistant API. Always sends the current
   * user's JWT and appends their own entity_id to the query string, so a
   * request can only ever touch the caller's own data.
   *
   * On a non-2xx response it extracts the backend's error/detail text (e.g.
   * SSRF blocked-host validation) and throws an AssistantApiError carrying a
   * UI-safe message + status — never raw HTTP/stack noise. Returns parsed JSON
   * (or {} for empty bodies, e.g. 204 on DELETE).
   */
  private async apiRequest<T>(
    method: 'GET' | 'POST' | 'PUT' | 'DELETE',
    path: string,
    opts: { query?: Record<string, string>; body?: any } = {},
  ): Promise<T> {
    const entityId = this.getEntityId();
    if (!entityId) {
      throw new AssistantApiError('You are not signed in. Please refresh and try again.', 401);
    }
    // entity_id is applied LAST so a caller's query map can never override the
    // authenticated user's own scope (tenant isolation: own entity only).
    const params = new URLSearchParams({ ...(opts.query || {}), entity_id: entityId });
    const url = `${environment.assistantApiUrl}${path}?${params.toString()}`;
    const headers: Record<string, string> = { Authorization: `Bearer ${this.getJwt()}` };
    const init: RequestInit = { method, headers, cache: 'no-store' };
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }

    let res: Response;
    try {
      res = await fetch(url, init);
    } catch (_) {
      throw new AssistantApiError('Could not reach the server. Check your connection and try again.', 0);
    }

    if (!res.ok) {
      let detail = '';
      try {
        const j: any = await res.clone().json();
        detail = (j && (j.detail || j.error || j.message)) || '';
      } catch (_) {
        try { detail = await res.text(); } catch (_) {}
      }
      // Don't surface huge HTML error pages — cap and fall back to a generic line.
      if (!detail || detail.length > 300 || /<\/?[a-z][\s\S]*>/i.test(detail)) {
        detail = res.status === 401 || res.status === 403
          ? 'Your session has expired — please refresh the page.'
          : 'Something went wrong. Please try again.';
      }
      throw new AssistantApiError(String(detail), res.status);
    }

    if (res.status === 204) { return {} as T; }
    try {
      return (await res.json()) as T;
    } catch (_) {
      return {} as T;
    }
  }

  // ── Skills CRUD ────────────────────────────────────────────────────────────

  async listSkills(): Promise<AssistantSkill[]> {
    const data = await this.apiRequest<{ skills?: AssistantSkill[] } | AssistantSkill[]>(
      'GET', '/v1/assistant/skills',
    );
    return Array.isArray(data) ? data : (data.skills || []);
  }

  createSkill(skill: Partial<AssistantSkill>): Promise<AssistantSkill> {
    return this.apiRequest<AssistantSkill>('POST', '/v1/assistant/skills', { body: skill });
  }

  updateSkill(skillId: string, patch: Partial<AssistantSkill>): Promise<AssistantSkill> {
    return this.apiRequest<AssistantSkill>(
      'PUT', `/v1/assistant/skills/${encodeURIComponent(skillId)}`, { body: patch },
    );
  }

  deleteSkill(skillId: string): Promise<void> {
    return this.apiRequest<void>('DELETE', `/v1/assistant/skills/${encodeURIComponent(skillId)}`);
  }

  // ── Secrets (names only; values never returned by the backend) ─────────────

  async listSecrets(): Promise<string[]> {
    const data = await this.apiRequest<{ secrets?: string[]; names?: string[] } | string[]>(
      'GET', '/v1/assistant/secrets',
    );
    if (Array.isArray(data)) {
      return data.map(s => (typeof s === 'string' ? s : (s as any).name)).filter(Boolean);
    }
    return data.secrets || data.names || [];
  }

  createSecret(name: string, value: string): Promise<void> {
    return this.apiRequest<void>('POST', '/v1/assistant/secrets', { body: { name, value } });
  }

  deleteSecret(name: string): Promise<void> {
    return this.apiRequest<void>('DELETE', `/v1/assistant/secrets/${encodeURIComponent(name)}`);
  }

  // ── Routines (list + delete; creation is via chat) ─────────────────────────

  async listRoutines(): Promise<AssistantRoutine[]> {
    const data = await this.apiRequest<{ routines?: AssistantRoutine[] } | AssistantRoutine[]>(
      'GET', '/v1/assistant/routines',
    );
    return Array.isArray(data) ? data : (data.routines || []);
  }

  deleteRoutine(routineId: string): Promise<void> {
    return this.apiRequest<void>('DELETE', `/v1/assistant/routines/${encodeURIComponent(routineId)}`);
  }

  // ── Admin capabilities ─────────────────────────────────────────────────────

  /** GET /v1/assistant/admin/capabilities — returns capability list (AGENT_ADMIN only). */
  listAdminCapabilities(): Promise<AdminCapability[]> {
    return this.apiRequest<AdminCapability[] | { capabilities?: AdminCapability[] }>(
      'GET', '/v1/assistant/admin/capabilities',
    ).then(data => Array.isArray(data) ? data : (data.capabilities || []));
  }

  /** PUT /v1/assistant/admin/capabilities — toggle a capability on/off. */
  setAdminCapability(capabilityKey: string, enabled: boolean): Promise<void> {
    return this.apiRequest<void>('PUT', '/v1/assistant/admin/capabilities', {
      body: { entity_id: this.getEntityId(), capability_key: capabilityKey, enabled },
    });
  }

  // ── Credit / usage ─────────────────────────────────────────────────────────

  usage(period: 'today' | 'week' | 'month' = 'today'): Promise<UsageReport> {
    return this.apiRequest<UsageReport>('GET', '/v1/assistant/usage', { query: { period } });
  }

  // ── Memory ─────────────────────────────────────────────────────────────────

  getMemory(): Promise<any> {
    return this.apiRequest<any>('GET', '/v1/assistant/memory');
  }

  clearMemory(): Promise<void> {
    return this.apiRequest<void>('DELETE', '/v1/assistant/memory');
  }
}
